import React from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import { useAutoTranslation } from 'src/hooks/useTranslate';

function getStyles(name, personName, theme) {
  return {
    fontWeight:
      personName.indexOf(name) === -1
        ? theme.typography.fontWeightRegular
        : theme.typography.fontWeightMedium
  };
}

//this is added to make options under the select
const ITEM_HEIGHT = 36;
const VISIBLE_ITEMS = 8;
// A checkbox row is taller, so the five-row cap has to grow with it or fewer
// than five options are visible and the list looks arbitrarily truncated.
const EXPLICIT_ITEM_HEIGHT = 46;
      
const menuPropsFor = (explicitSelection) => ({
  slotProps: {
    paper: {
      style: {
       maxHeight:
          (explicitSelection ? EXPLICIT_ITEM_HEIGHT : ITEM_HEIGHT) *
          VISIBLE_ITEMS
      }
    }
  },
  disableScrollLock: true
});

// supports only 1d values
// options e.g. ['a', 'b']
// mapper e.g. {'a': 'A', 'b': 'B'}
//
// `explicitSelection` (TGCLOUD-1650) opts a call site into an unambiguous
// multi-select affordance: a checkbox per row, a primary-tinted selected row,
// deletable chips, and a menu that STAYS OPEN when you add something.
//
// It is a prop, defaulted off, rather than the new default, because this
// component has ~19 call sites — seven clinical-notes forms, the FHQ diagnosis
// pickers, pregnancies, and the pedigree sidebar — and `setOpen(false)` on add
// is load-bearing for `onNewSelection`, which PersonalDetailsForm and
// FamilyDetailsForm use to open a follow-up dialog after a new diagnosis is
// picked. Flipping the default is a worthwhile follow-up once those sites have
// been browser-QA'd; it is not this ticket.
export default function MultipleSelect(props) {
  const theme = useTheme();

  const t = useAutoTranslation();

  const {
    value,
    name,
    onChange,
    onNewSelection,
    options,
    variant,
    mapper,
    disabled = false,
    explicitSelection = false
  } = props;

  const [open, setOpen] = React.useState(false);

  const handleChange = (e) => {
    const newValues = e.target.value;
    const added = newValues.find((v) => !value.includes(v));
    onChange(e);
    if (added) {
      // Closing on add reads as a radio-style REPLACE: the list vanishes and the
      // only evidence that something was added rather than swapped is a second
      // chip in a field the user is no longer looking at. That is most of why
      // the specialty picker was reported as unintuitive.
      if (!explicitSelection) setOpen(false);
      if (onNewSelection) onNewSelection(added);
    }
  };

  // Removing a chip has to look like the change event the parent expects, since
  // callers read `e.target.value` (and some read `e.target.name`).
  const handleDelete = (el) =>
    onChange({
      target: { name, value: value.filter((v) => v !== el) }
    });

  const label = (el) => (mapper?.[el] ? t(mapper[el]) : el);

  return (
    <FormControl variant={variant ?? 'outlined'} fullWidth>
      <Select
        sx={{
          '& .MuiSelect-icon': {
            //red color
            // color: theme.palette.primary.main,
            fontSize: 30
          }
        }}
        disabled={disabled}
        multiple
        value={value}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onChange={handleChange}
        inputProps={{
          name: name,
          id: name
        }}
        renderValue={(selected) => (
          <div className="chips">
            {selected.map((el) => (
              // A value missing from the mapper used to render an EMPTY fragment,
              // so a selection was stored but invisible — the field looked unset.
              // That bit whenever the mapper was a fixed list and the value was
              // not in it (for example a disease tagged to an admin-created
              // specialty). Fall back to the raw value so a selection is always
              // visible, even if unlabelled. See remediation plan W5/R7.
              <Chip
                size="small"
                key={el}
                label={label(el)}
                className="chip"
                {...(explicitSelection && !disabled
                  ? {
                      onDelete: () => handleDelete(el),
                      // Without this the Select swallows the pointer event and
                      // opens the menu instead of deleting the chip.
                      onMouseDown: (e) => e.stopPropagation()
                    }
                  : {})}
              />
            ))}
          </div>
        )}
        MenuProps={menuPropsFor(explicitSelection)}
      >
        {options.map((el) =>
          explicitSelection ? (
            <MenuItem
              key={el}
              value={el}
              sx={{
                // MUI's default selected state is an 8% neutral wash, which is
                // the same grey family as hover — so "selected" and "my cursor
                // is here" looked identical. Tint with the primary colour so the
                // two read differently.
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08)
                },
                '&.Mui-selected:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.16)
                }
              }}
            >
              <Checkbox
                size="small"
                checked={value.indexOf(el) > -1}
                sx={{ mr: 1, p: 0.5 }}
              />
              <ListItemText primary={label(el)} />
            </MenuItem>
          ) : (
            <MenuItem key={el} value={el} style={getStyles(el, value, theme)}>
              {t(mapper[el])}
            </MenuItem>
          )
        )}
      </Select>
    </FormControl>
  );
}
